/**
 * Learnica's name and face.
 *
 * The logo files live in `public/brand/`, made from the originals Qatar Care
 * supplied (LEARNICA.zip): trimmed, sized for the web, and a white copy cut
 * from the same shape for dark backgrounds. The masters — vector PDFs and the
 * full-size artwork — are in Google Drive, Codex-Workspace/Projects/Learnica/
 * stationary. Make any new size from those, not from these.
 *
 * The programmes are written here once, because they say what Learnica is:
 * the sign-in page lists them for somebody who has never been here, and every
 * other page that needs them takes them from here rather than restating them.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * The Learnica logo: the check-mark figure and the wordmark, in the brand
 * lavender. `light` is the same shape in white, for slate and anything dark —
 * the way the logo appears on Qatar Care's own Learnica stationery.
 */
export function Logo({ size = 'large', light = false }: { size?: 'large' | 'small'; light?: boolean }) {
  return (
    <img
      className={`logo logo--${size}`}
      src={light ? '/brand/learnica-logo-white.png' : '/brand/learnica-logo.png'}
      alt="Learnica"
    />
  );
}

/**
 * What Learnica teaches, in the order the owner names them. Each line says
 * what is actually in the courses — the review goes system by system with a
 * test after each unit and a final exam; the English series runs from first
 * lessons to "Empowered to Care: English for Nurses".
 */
export const PROGRAMMES = [
  {
    name: 'Healthcare training',
    about: 'Live classes with your trainers, weekly assessments and the fundamentals of practice.',
  },
  {
    name: 'English for healthcare',
    about: 'From your first lessons to talking with patients and colleagues on the ward.',
  },
  {
    name: 'Prometric review',
    about: 'Body system by body system, with a test after each unit and a final exam to practise on.',
  },
] as const;

/**
 * The frame for every page somebody sees before they are signed in: the form
 * on one side, what Learnica is on the other. On a phone the form comes first,
 * because signing in is why somebody is here.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="gate">
      <main className="gate__form">
        <Link to="/" className="gate__logo"><Logo /></Link>
        {children}
      </main>

      <aside className="gate__panel" aria-label="What Learnica teaches">
        {/* White on slate, as on the Learnica stationery. Shown here on wide
            screens only; on a phone the logo above the form is the one. */}
        <Logo light />
        <p className="gate__lede">Qatar Care's training for nurses preparing to work in Qatar.</p>
        <ul className="programmes">
          {PROGRAMMES.map((p) => (
            <li key={p.name} className="programme">
              <h2 className="programme__name">{p.name}</h2>
              <p className="programme__about">{p.about}</p>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
